export interface AssessmentData {
  administration_date: string;
  adaptive_behavior_composite_standard_score: number | null;
  adaptive_behavior_composite_percentile: number | null;
  communication_score: number | null;
  communication_percentile: number | null;
  daily_living_score: number | null;
  daily_living_percentile: number | null;
  socialization_score: number | null;
  socialization_percentile: number | null;
  motor_score: number | null;
  motor_percentile: number | null;
  behavior_internalizing_v_scale: number | null;
  behavior_externalizing_v_scale: number | null;
  total_number_of_critical_items_scored: number | null;
  Examiner?: string;
  assessment_type?: string;
  type?: string;
  [key: string]: string | number | null | undefined;
}

export interface AssessmentRecord {
  id?: string | number;
  client_id: string;
  evaluation_date: string;
  created: string;
  last_updated: string;
  created_by: string;
  last_updated_by: string;
  assessment_type?: string;
  type?: string;
  assessment_name?: string;
  data: AssessmentData;
}

export type AssessmentType =
  | "vineland"
  | "cfql2"
  | "vbmappMilestones"
  | "afls"
  | "vbmappBarriers"
  | "vbmappTransition"
  | "sociallySavvy"
  | "srs2"
  | "peak"
  | "efl"
  | "qabf"
  | "cpfq";

export type AssessmentBannerAction = "start" | "add";

export type AssessmentChartVariant = "line" | "bar" | "stackedMixed";

export interface AssessmentDataset {
  label: string;
  data: Array<number | null>;
  dataKey?: string;
  borderColor?: string;
  backgroundColor?: string;
  type?: "line" | "bar";
  yAxisID?: string;
  stack?: string;
  /** Appended to the raw value in the tooltip, e.g. "/9" → "5/9" or " / 16" → "5 / 16". */
  tooltipValueSuffix?: string;
}

export interface AssessmentChartConfig {
  title?: string;
  description?: string;
  variant: AssessmentChartVariant;
  yAxisLabels?: [string] | [string, string];
  datasets: AssessmentDataset[];
  /** Label prefix rendered left of the line-series legend in mixed charts. */
  legendLineLabel?: string;
  /** Label prefix rendered left of the bar-series legend in mixed charts. */
  legendBarLabel?: string;
}

export interface AssessmentResultField {
  key: string;
  label: string;
  /** Auto-calculated display value derived from assessment data (frontend-only, never stored). */
  compute?: (data: AssessmentData) => string | number | null;
  /** Optional tooltip configuration for displaying contextual help when score is below threshold. */
  tooltip?: {
    /** Show tooltip when value is <= this threshold. */
    threshold: number;
    /** Helper text to display in the tooltip. */
    text: string;
  };
}

export interface AssessmentResultSection {
  title: string;
  fields: AssessmentResultField[];
  /**
   * When true, the first field of this section is rendered as a prominent
   * section-level header (score + label) rather than a regular grid row.
   * Used for AFLS domain sections where each domain total leads the section.
   */
  showSectionHero?: boolean;
}

export interface AssessmentDefinition {
  type: AssessmentType;
  title: string;
  description: string;
  primaryActionLabel: string;
  secondaryActionLabel?: string;
  secondaryAction?: AssessmentBannerAction;
  filloutUrl?: string;
  chart: AssessmentChartConfig;
  resultSections: AssessmentResultSection[];
  /** When true, the page-level hero (first field of first section) is not extracted. */
  skipHero?: boolean;
}

export interface DomainScoreConfig {
  key: keyof AssessmentData;
  label: string;
  graphLabel: string;
  borderColor: string;
  backgroundColor: string;
}

// ─── SRS-2 Enums (aligned with API spec) ────────────────────────────────────

export type SrsFormAdministered =
  | "AdultRelativeOtherReport"
  | "AdultSelfReport"
  | "PreschoolOnline"
  | "SchoolAgeOnline";

export const SRS_FORM_OPTIONS: Array<{ value: SrsFormAdministered; label: string }> = [
  { value: "AdultRelativeOtherReport", label: "Adult relative / other report" },
  { value: "AdultSelfReport", label: "Adult self report" },
  { value: "PreschoolOnline", label: "Preschool (online)" },
  { value: "SchoolAgeOnline", label: "School age (online)" },
];

export type SrsSeverityRange = "Normal" | "Mild" | "Moderate" | "Severe";

export const SRS_SEVERITY_OPTIONS: Array<{ value: SrsSeverityRange; label: string }> = [
  { value: "Normal", label: "Normal" },
  { value: "Mild", label: "Mild" },
  { value: "Moderate", label: "Moderate" },
  { value: "Severe", label: "Severe" },
];

// ─── Data Entry Form Data Interfaces ────────────────────────────────────────

export interface VbmappMilestonesFormData {
  administrationDate: string | null;
  administratorName: string;
  mand: number | null;
  tact: number | null;
  listenerResponding: number | null;
  visualPerceptualMts: number | null;
  play: number | null;
  social: number | null;
  motorImitation: number | null;
  echoic: number | null;
  vocal: number | null;
  listenerRespondingLrffc: number | null;
  intraverbal: number | null;
  group: number | null;
  linguistics: number | null;
  reading: number | null;
  writing: number | null;
  math: number | null;
  /** Dropzone file wrappers for the milestones grid upload — not stored directly; resolved to a document ID server-side. */
  uploadedFiles: { file: File; errors: boolean }[];
}

export interface Srs2FormData {
  administrationDate: string | null;
  administratorName: string;
  formAdministered: SrsFormAdministered | null;
  respondentName: string | null;
  relationshipToClient: string | null;
  totalTScore: number | null;
  awarenessTScore: number | null;
  cognitionTScore: number | null;
  communicationTScore: number | null;
  motivationTScore: number | null;
  srsSciTScore: number | null;
  rirbTScore: number | null;
  // Severity/level fields are derived from T-scores in the UI and included in the
  // API payload — they are present in the srs2_assessment schema.
}

export interface PeakFormData {
  administrationDate: string | null;
  administratorName: string;
  foundationalLearning: number | null;
  perceptualLearning: number | null;
  verbalComprehension: number | null;
  verbalReasoningMemory: number | null;
  foundationalLearningBasicSocialSkills: number | null;
  basicVerbalComprehensionAdvancedSocialSkills: number | null;
  advancedVerbalComprehensionAdvancedMathSkills: number | null;
  verbalReasoningAdvancedReadingWritingSkills: number | null;
  reflexivity: number | null;
  symmetry: number | null;
  transitivity: number | null;
  equivalence: number | null;
  nonArbitraryLevel1: number | null;
  culturalLevel2: number | null;
  arbitraryLevel3: number | null;
  complexLevel4: number | null;
  /** One dropzone file wrapper per PEAK module (DT, GEN, EQ, TR) */
  uploadedFilesDt: { file: File; errors: boolean }[];
  uploadedFilesGen: { file: File; errors: boolean }[];
  uploadedFilesEq: { file: File; errors: boolean }[];
  uploadedFilesTr: { file: File; errors: boolean }[];
}

// ─── AFLS Multi-Step Form Types ──────────────────────────────────────────────

export interface AflsSkillItem {
  id: string;
  name: string;
  score: number | null;
  maxScore: number;
  /** True for the auto-calculated domain total row. Never stored in backend directly. */
  isTotal?: boolean;
  /** camelCase API field name used when sending sub-skill scores to the backend. Present on non-total skills only. */
  apiField?: string;
}

export interface AflsStepConfig {
  id: number;
  title: string;
  question: string;
  scoreRange: string;
  /** camelCase key for the domain total sent in the API payload. */
  domainTotalField: string;
  skills: Omit<AflsSkillItem, "score">[];
}

export interface AflsStepState {
  stepId: number;
  stepCompleted: boolean;
  skills: AflsSkillItem[];
}

export interface AflsFormData {
  administrationDate: string | null;
  administratorName: string;
  steps: AflsStepState[];
  /** Dropzone file wrappers for the AFLS scoring grid upload — not stored directly; resolved to a document ID server-side. */
  uploadedFiles: { file: File; errors: any[] }[];
}
